import { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import GeneralExpenseForm from "./_components/GeneralExpenseForm";
import {
  extractFleetMediaFilename,
  getFleetUploadRelativeUrl,
} from "../../../../api/fleet-management/api";
import {
  useDeleteMediaMutation,
  useUploadMediaMutation,
} from "../../../../api/fleet-management/media/media.hooks";
import {
  useCreateGeneralExpenseMutation,
  useGetGeneralExpenseById,
  useUpdateGeneralExpenseMutation,
} from "../../../../api/fleet-management/general-expenses/general-expenses.hooks";
import { useGetVehicles } from "../../../../api/fleet-management/vehicles/vehicles.hooks";
import { useGetDrivers } from "../../../../api/fleet-management/drivers/drivers.hooks";
import {
  GENERAL_EXPENSE_TYPE_OPTIONS,
  buildGeneralExpensePayload,
  initialGeneralExpenseForm,
  mapGeneralExpenseToForm,
  validateGeneralExpenseForm,
} from "./utils/general-expense.utils";

const GeneralExpenseFormPage = () => {
  const history = useHistory();
  const { generalExpenseId } = useParams();
  const isEditMode = Boolean(generalExpenseId);

  const [form, setForm] = useState(initialGeneralExpenseForm);
  const [initialFormState, setInitialFormState] = useState(initialGeneralExpenseForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [mediaFeedback, setMediaFeedback] = useState({ type: "", message: "" });

  const {
    data: expenseData,
    isLoading: isExpenseLoading,
    isError: isExpenseError,
    error: expenseError,
  } = useGetGeneralExpenseById(generalExpenseId);

  const {
    mutate: createGeneralExpense,
    isLoading,
    isPending,
    error,
    reset,
  } = useCreateGeneralExpenseMutation();

  const {
    mutate: updateGeneralExpense,
    isLoading: isUpdating,
    isPending: isUpdatePending,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateGeneralExpenseMutation();

  const { mutateAsync: uploadMedia, isLoading: isUploadingReceipt } = useUploadMediaMutation();
  const { mutateAsync: deleteMedia, isLoading: isDeletingReceipt } = useDeleteMediaMutation();

  const { data: vehiclesData, isLoading: isVehiclesLoading } = useGetVehicles();
  const { data: driversData, isLoading: isDriversLoading } = useGetDrivers();

  useEffect(() => {
    if (!isEditMode || !expenseData) return;
    const nextForm = mapGeneralExpenseToForm(expenseData);
    setForm(nextForm);
    setInitialFormState(nextForm);
  }, [expenseData, isEditMode]);

  const submitting = isLoading || isPending || isUpdating || isUpdatePending;
  const submitError = isEditMode ? updateError : error;

  const vehicles = Array.isArray(vehiclesData)
    ? vehiclesData
    : vehiclesData?.vehicles || vehiclesData?.items || [];

  const drivers = Array.isArray(driversData)
    ? driversData
    : driversData?.drivers || driversData?.items || [];

  const updateField = (key, value) => {
    setForm((prev) => {
      if (key === "expenseType") {
        return {
          ...prev,
          expenseType: value,
          vehicleId: value === "vehicle" ? prev.vehicleId : "",
          driverId: value === "driver" ? prev.driverId : "",
        };
      }

      return { ...prev, [key]: value };
    });

    setValidationErrors((prev) => {
      const nextErrors = { ...prev, [key]: undefined };
      if (key === "expenseType") {
        nextErrors.vehicleId = undefined;
        nextErrors.driverId = undefined;
      }
      return nextErrors;
    });
  };

  const handleUploadReceipt = async (file) => {
    if (!file) return;

    setMediaFeedback({ type: "", message: "" });

    try {
      const uploadResult = await uploadMedia(file);
      const relativePath = getFleetUploadRelativeUrl(uploadResult, 0);

      if (!relativePath) {
        throw new Error("Upload response did not include media path.");
      }

      updateField("receipt", relativePath);
      setMediaFeedback({ type: "success", message: "Receipt uploaded successfully." });
    } catch (uploadError) {
      setMediaFeedback({
        type: "danger",
        message: uploadError?.response?.data?.message || "Failed to upload receipt.",
      });
    }
  };

  const handleRemoveReceipt = async () => {
    const receiptPath = form.receipt;
    if (!receiptPath) return;

    setMediaFeedback({ type: "", message: "" });

    const filename = extractFleetMediaFilename(receiptPath);
    if (!filename) {
      updateField("receipt", "");
      return;
    }

    try {
      await deleteMedia({ filename });
      updateField("receipt", "");
      setMediaFeedback({ type: "success", message: "Receipt removed successfully." });
    } catch (deleteError) {
      setMediaFeedback({
        type: "danger",
        message: deleteError?.response?.data?.message || "Failed to remove receipt.",
      });
    }
  };

  const validate = () => {
    const nextErrors = validateGeneralExpenseForm(form);
    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    reset();
    resetUpdate();

    if (!validate()) return;

    const payload = buildGeneralExpensePayload(form);

    if (isEditMode) {
      updateGeneralExpense(
        { generalExpenseId, generalExpenseData: payload },
        {
          onSuccess: () => {
            history.push("/fleet-management/general-expenses");
          },
        }
      );
      return;
    }

    createGeneralExpense(payload, {
      onSuccess: () => {
        history.push("/fleet-management/general-expenses");
      },
    });
  };

  if (isEditMode && isExpenseLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && isExpenseError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {expenseError?.response?.data?.message || "Failed to load general expense details."}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => history.push("/fleet-management/general-expenses")}
              aria-label="Back to general expenses list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">
              {isEditMode ? "Edit General Expense" : "Create General Expense"}
            </h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            {isEditMode
              ? "Update general expense details."
              : "Fill in general expense details."}
          </p>

          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError?.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} general expense. Please try again.`}
            </Alert>
          )}

          {mediaFeedback.message && (
            <Alert variant={mediaFeedback.type} className="mb-4">
              {mediaFeedback.message}
            </Alert>
          )}

          <GeneralExpenseForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            isEditMode={isEditMode}
            expenseTypeOptions={GENERAL_EXPENSE_TYPE_OPTIONS}
            vehicles={vehicles}
            drivers={drivers}
            isVehiclesLoading={isVehiclesLoading}
            isDriversLoading={isDriversLoading}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onUploadReceipt={handleUploadReceipt}
            onRemoveReceipt={handleRemoveReceipt}
            isUploadingReceipt={isUploadingReceipt}
            isDeletingReceipt={isDeletingReceipt}
            onBack={() => {
              history.push("/fleet-management/general-expenses");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralExpenseFormPage;
